import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Button from '../../../components/ui/Button';
import LoadingMark from '../../../components/ui/LoadingMark';
import { ModalHeader, ModalShell } from '../../../components/ui/ModalFrame';
import { MODAL_LAYER } from '../../../constants/modalLayers';
import { MODAL_DIALOG_ACTIONS_END } from '../../../constants/layoutSpacing';
import {
  fetchSignatureStatus,
  renewSignatureExpiration,
  resendSignatureNotification,
} from '../../../services/signatureService';
import { normalizeDocumentStatus } from '../../../utils/documentStatusMeta';
import { formatTelefoneMask } from '../../../utils/signatureFieldModel';

const STATUS_LABELS = {
  pending: 'Pendente',
  viewed: 'Visualizou',
  signed: 'Assinou',
  rejected: 'Recusou',
};

const STATUS_CLASSES = {
  pending: 'bg-muted text-muted-foreground',
  viewed: 'bg-warning/15 text-warning',
  signed: 'bg-success/15 text-success',
  rejected: 'bg-destructive/15 text-destructive',
};

const RENEW_DAYS = 14;

function channelLabel(channel) {
  const value = String(channel || '').toLowerCase();
  if (value === 'whatsapp') return 'WhatsApp';
  return 'E-mail';
}

function formatDateTimePt(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isExpired(expiresAt) {
  if (!expiresAt) return false;
  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() < Date.now();
}

function isExpiringSoon(expiresAt, days = 3) {
  if (!expiresAt) return false;
  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) return false;
  const diffMs = date.getTime() - Date.now();
  return diffMs >= 0 && diffMs <= days * 24 * 60 * 60 * 1000;
}

/**
 * @param {{
 *   open: boolean;
 *   document: object | null;
 *   onClose: () => void;
 * }} props
 */
const SignatureStatusModal = ({ open, document, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [resendingAll, setResendingAll] = useState(false);
  const [renewing, setRenewing] = useState(false);

  const jobId = document?.job_id ? String(document.job_id) : '';
  const title =
    document?.name ||
    document?.title ||
    document?.document_title ||
    'Documento';
  const readOnly =
    normalizeDocumentStatus(document?.status) === 'SIGNED';

  const loadStatus = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    setError('');
    try {
      const result = await fetchSignatureStatus(jobId);
      if (!result.ok) {
        setPayload(null);
        setError(result.messagePt || 'Não foi possível carregar o status.');
        return;
      }
      setPayload(result.data || null);
    } catch (err) {
      console.error('SignatureStatusModal load:', err);
      setPayload(null);
      setError('Não foi possível carregar o status.');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (!open) {
      setPayload(null);
      setError('');
      setResendingId(null);
      setResendingAll(false);
      setRenewing(false);
      return;
    }
    void loadStatus();
  }, [open, loadStatus]);

  const handleResendOne = async (signerId) => {
    if (readOnly || !jobId || !signerId) return;
    setResendingId(signerId);
    try {
      const result = await resendSignatureNotification(jobId, signerId);
      if (!result.ok) {
        toast.error(result.messagePt || 'Falha ao reenviar.');
        return;
      }
      toast.success('Notificação reenviada.');
      await loadStatus();
    } catch (err) {
      console.error('handleResendOne:', err);
      toast.error('Falha ao reenviar.');
    } finally {
      setResendingId(null);
    }
  };

  const handleResendAll = async () => {
    if (readOnly || !jobId) return;
    setResendingAll(true);
    try {
      const result = await resendSignatureNotification(jobId);
      if (!result.ok) {
        toast.error(result.messagePt || 'Falha ao reenviar para todos.');
        return;
      }
      const count = Number(result.data?.resent_count || 0);
      toast.success(
        count > 0
          ? `Reenviado para ${count} signatário(s).`
          : 'Notificações reenviadas.'
      );
      await loadStatus();
    } catch (err) {
      console.error('handleResendAll:', err);
      toast.error('Falha ao reenviar para todos.');
    } finally {
      setResendingAll(false);
    }
  };

  const handleRenewExpiration = async () => {
    if (readOnly || !jobId) return;
    setRenewing(true);
    try {
      const result = await renewSignatureExpiration(jobId, RENEW_DAYS);
      if (!result.ok) {
        toast.error(result.messagePt || 'Falha ao renovar o prazo.');
        return;
      }
      const renewedAt =
        result.data?.expires_at || result.data?.expiration || null;
      if (renewedAt) {
        setPayload((prev) =>
          prev ? { ...prev, expires_at: renewedAt } : { expires_at: renewedAt }
        );
      }
      toast.success(`Prazo renovado por mais ${RENEW_DAYS} dias.`);
      await loadStatus();
      if (renewedAt) {
        setPayload((prev) => {
          if (!prev?.expires_at) {
            return { ...prev, expires_at: renewedAt };
          }
          const prevMs = Date.parse(prev.expires_at);
          const nextMs = Date.parse(renewedAt);
          if (
            Number.isFinite(nextMs) &&
            (!Number.isFinite(prevMs) || nextMs > prevMs)
          ) {
            return { ...prev, expires_at: renewedAt };
          }
          return prev;
        });
      }
    } catch (err) {
      console.error('handleRenewExpiration:', err);
      toast.error('Falha ao renovar o prazo.');
    } finally {
      setRenewing(false);
    }
  };

  if (!open) return null;

  const signers = Array.isArray(payload?.signers) ? payload.signers : [];
  const channel = payload?.notification_channel || 'Email';
  const progress = payload?.progress || {};
  const expiresAt = payload?.expires_at;
  const expiresLabel = formatDateTimePt(expiresAt);
  const expired = isExpired(expiresAt);
  const expiringSoon = isExpiringSoon(expiresAt);
  const canResendAny = !readOnly && signers.some((s) => s?.can_resend);
  const busy =
    Boolean(resendingId) || resendingAll || renewing || loading;

  return (
    <ModalShell
      zClass={MODAL_LAYER.standard}
      backdropClassName="bg-black/50"
      panelClassName="bg-card rounded-lg max-w-lg w-full max-h-[min(90dvh,44rem)] flex flex-col shadow-elevation-lg overflow-hidden"
    >
      <ModalHeader
        title={readOnly ? 'Histórico das assinaturas' : 'Status das assinaturas'}
        onClose={busy ? undefined : onClose}
      />

      <div className="px-4 md:px-6 pt-3 pb-3 space-y-2 border-b border-border">
        <p className="text-sm font-medium text-foreground truncate" title={title}>
          {title}
        </p>
        <p className="text-sm text-muted-foreground">
          Canal de envio:{' '}
          <span className="font-medium text-foreground">{channelLabel(channel)}</span>
        </p>

        {progress?.total > 0 ? (
          <p className="text-sm text-muted-foreground">
            Progresso:{' '}
            <span className="font-medium text-foreground">
              {progress.signed || 0}/{progress.total} assinaram
            </span>
            {progress.viewed ? ` · ${progress.viewed} visualizou` : ''}
            {progress.pending ? ` · ${progress.pending} pendente` : ''}
            {progress.rejected ? ` · ${progress.rejected} recusou` : ''}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 justify-between">
          <p
            className={`text-sm ${
              !readOnly && expired
                ? 'text-destructive'
                : !readOnly && expiringSoon
                  ? 'text-warning'
                  : 'text-muted-foreground'
            }`}
          >
            {expiresLabel
              ? readOnly
                ? `Prazo da solicitação: ${expiresLabel}`
                : expired
                  ? `Prazo expirado em ${expiresLabel}`
                  : `Expira em ${expiresLabel}`
              : 'Prazo de assinatura não informado'}
          </p>
          {!readOnly ? (
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={handleRenewExpiration}
              disabled={busy}
              loading={renewing}
            >
              Renovar prazo (+{RENEW_DAYS} dias)
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-4">
        {loading ? (
          <div className="flex justify-center py-10">
            <LoadingMark message="Carregando status…" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            {error}
          </div>
        ) : signers.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            Nenhum signatário encontrado para este documento.
          </p>
        ) : (
          <ul className="space-y-3">
            {signers.map((signer) => {
              const status = STATUS_LABELS[signer.status] || STATUS_LABELS.pending;
              const statusClass =
                STATUS_CLASSES[signer.status] || STATUS_CLASSES.pending;
              const contact =
                String(channel).toLowerCase() === 'whatsapp'
                  ? signer.telefone
                    ? formatTelefoneMask(signer.telefone)
                    : '—'
                  : signer.email || '—';
              const viewedAt = formatDateTimePt(signer.viewed_at);
              const signedAt = formatDateTimePt(signer.signed_at);
              const rejectedAt = formatDateTimePt(signer.rejected_at);

              return (
                <li
                  key={signer.signer_id}
                  className="rounded-lg border border-border p-3 space-y-2"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {signer.step != null ? `${signer.step}. ` : ''}
                        {signer.full_name || 'Signatário'}
                      </p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {contact}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusClass}`}
                    >
                      {status}
                    </span>
                  </div>

                  <div className="space-y-0.5 text-xs text-muted-foreground">
                    {signer.notified ? <p>Notificação enviada</p> : <p>Ainda não notificado</p>}
                    {viewedAt ? <p>Visualizou em {viewedAt}</p> : null}
                    {signedAt ? <p>Assinou em {signedAt}</p> : null}
                    {rejectedAt ? <p>Recusou em {rejectedAt}</p> : null}
                    {signer.rejection_reason ? (
                      <p className="text-destructive">
                        Motivo: {signer.rejection_reason}
                      </p>
                    ) : null}
                  </div>

                  {!readOnly && signer.can_resend ? (
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        onClick={() => handleResendOne(signer.signer_id)}
                        disabled={busy}
                        loading={resendingId === signer.signer_id}
                      >
                        Reenviar para assinatura
                      </Button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div
        className={`${MODAL_DIALOG_ACTIONS_END} border-t border-border px-4 md:px-6 py-4 flex-wrap`}
      >
        <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
          Fechar
        </Button>
        {!readOnly ? (
          <Button
            type="button"
            onClick={handleResendAll}
            disabled={busy || !canResendAny}
            loading={resendingAll}
          >
            Reenviar para todos
          </Button>
        ) : null}
      </div>
    </ModalShell>
  );
};

export default SignatureStatusModal;
